import BubbleAsk from '@/assets/lottie/bubble-ask.json';
import BreadCrumbs from '@/components/atom/BreadCrumbs';
import CustomLottieIcon from '@/components/atom/CustomLottieIcon';
import StickyButton from '@/components/atom/buttons/StickyButton';
import CategoryBottomSheet from '@/components/common/communication/qna/CategoryBottomSheet';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import QnaAgreementInfoBox from '@/components/molecules/qna-agreement-info-box';
import StickyAppHeader from '@/components/molecules/sticky-app-header';

import { QnaWriteMobileViewModel } from '@/hooks/qna/useQnaWriteMobileViewModel';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { Button } from 'doctornow-design-system';
import styled, { css } from 'styled-components';
import PolicyAgreementBottomSheet from './PolicyAgreementBottomSheet';
import QnaWriteCategory from './QnaWriteCategory';
import QnaWriteContentTextarea from './QnaWriteContentTextarea';
import QnaWriteVerified from './QnaWriteVerified';

interface Props extends CustomPageProps {
  qnaWriteMobileViewModel: QnaWriteMobileViewModel;
}

const QnaWriteMobileTemplate = ({ client, qnaWriteMobileViewModel: { ...viewModel } }: Props) => {
  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title='' />
        </TemplateLayout.Header>
      )}
      <TemplateLayout.Content>
        {client.clientType === ClientType.WEB && (
          <BreadCrumbs.Wrapper>
            <BreadCrumbs visible path={`${ROUTES.QNA.WRITE.path}`} type='qna' />
          </BreadCrumbs.Wrapper>
        )}
        <QuickAnswerWriteTemplateContainer>
          <TopTitleWrapper>
            <CustomLottieIcon width={40} height={40} animationData={BubbleAsk} />
            <TopTitle>{'건강 정보 AI 닥터나우에게\n건강 고민을 물어보세요'}</TopTitle>
          </TopTitleWrapper>
          <QuickAnswerWriteFormWrapper>
            <QnaWriteContentTextarea
              question={viewModel.question}
              onChangeQuestion={viewModel.setQuestion}
              placeholder={viewModel.selectedCategoriesBottomSheetTabItem?.contentPlaceholder}
            />
            <QnaWriteCategory
              selectedTags={viewModel.selectedTags}
              onChangeSelectedTags={viewModel.setSelectedTags}
              selectedCategoriesBottomSheetTabItem={viewModel.selectedCategoriesBottomSheetTabItem}
              showCategoriesBottomSheet={viewModel.showCategoriesBottomSheet}
              toggleCategoriesBottomSheet={viewModel.toggleCategoriesBottomSheet}
            />

            <QnaWriteVerified />
          </QuickAnswerWriteFormWrapper>
          <QuickAnswerWriteInfoBoxWrapper>
            <QnaAgreementInfoBox />
          </QuickAnswerWriteInfoBoxWrapper>
          <StickyButton>
            <Button
              size={'xl'}
              className={'w-full'}
              disabled={viewModel.isSubmitButtonDisabled}
              type='submit'
              onClick={viewModel.checkEnableSubmit}
            >
              물어보기
            </Button>
          </StickyButton>
          <CategoryBottomSheet
            isVisible={viewModel.showCategoriesBottomSheet}
            toggleBottomSheet={viewModel.toggleCategoriesBottomSheet}
            onClickCategoryItem={viewModel.onClickCategoriesBottomSheetTabListItem}
            categoryGroup={viewModel.categoryGroup}
            selectedTab={viewModel.selectedTab}
            onClickCategoriesBottomSheetTab={viewModel.onClickCategoriesBottomSheetTab}
            getSelectedTabItemList={viewModel.getSelectedTabItemList}
          />

          <PolicyAgreementBottomSheet
            isSubmitButtonDisabled={viewModel.isSubmitButtonDisabled}
            viewModel={viewModel.policyAgreementBottomSheetViewModel}
            afterSubmit={viewModel.requestAiAnswer}
          />
        </QuickAnswerWriteTemplateContainer>
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default QnaWriteMobileTemplate;

const QuickAnswerWriteTemplateContainer = styled.form`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
`;

const TopTitleWrapper = styled.div`
  padding: 16px 24px 40px;
  width: 100%;
`;

const TopTitle = styled.div`
  margin: 24px 0 4px;

  ${({ theme }) => css`
    ${theme.typography.UI22SB};
    color: ${theme.color.gray700};
    white-space: pre-wrap;
    `};
`;

const QuickAnswerWriteFormWrapper = styled.div`
  width: 100%;
  padding: 0 20px;
`;

const QuickAnswerWriteInfoBoxWrapper = styled.div`
  width: 100%;
`;
